import type { LocatesClient } from './interface';
import type {
    LocateInventoryPoolUploadRequest,
    LocateInventoryPoolUploadResponse,
    SecurityInventory,
    UploadableLocateProvider,
    UploadableLocateProviderResponse,
} from '@thinkalpha/ui-types/contracts/locates/inventory';
import type {
    LocateAcceptResponse,
    LocateCancelResponse,
    LocateLimitRequest,
    LocateLimitResponse,
    LocateOrdersForUserResponse,
    LocateRejectResponse,
    NewLocateOrderRequest,
    NewLocateOrderResponse,
    LocateOrder,
    NewLocateOrder,
    AutoAcceptInfo,
    ClearLocateOrdersResponse,
    ClearLocateOrdersRequest,
} from '@thinkalpha/ui-types/contracts/locates/orders';
import type {
    LocateAccount,
    LocateInjectRequest,
    LocateInjectResponse,
    LocatePlatformDiscoveryRequest,
    LocatePlatformDiscoveryResponse,
    LocateReceipt,
} from '@thinkalpha/ui-types/contracts/locates/platforms';
import type { LocateProvider, LocateProviderResponse } from '@thinkalpha/ui-types/contracts/locates/providers';
import type { AxiosInstance } from 'axios';
import { ReplaySubject, firstValueFrom, fromEvent, takeUntil } from 'rxjs';
import { createLegacyInstance } from 'src/api';
import { appConfig } from 'src/lib/config';

type LocatesApiAvailability =
    | { readonly status: 'authenticated'; readonly api: AxiosInstance }
    | { readonly status: 'unauthenticated' };

const apiAvailability = new ReplaySubject<LocatesApiAvailability>(1);

export class LocatesAuthenticationUnavailableError extends Error {
    constructor() {
        super('Locates API is unavailable without an authenticated session');
        this.name = 'LocatesAuthenticationUnavailableError';
    }
}

export const setAccessToken = (currentAccessToken: string | null): void => {
    apiAvailability.next(
        currentAccessToken === null
            ? { status: 'unauthenticated' }
            : {
                  status: 'authenticated',
                  api: createLegacyInstance({
                      baseURL: appConfig.locatesApi,
                      headers: { Authorization: `Bearer ${currentAccessToken}` },
                  }),
              },
    );
};

const getApi = async (abortSignal?: AbortSignal): Promise<AxiosInstance> => {
    if (abortSignal?.aborted) {
        throw abortSignal.reason;
    }

    const availability = await firstValueFrom(
        abortSignal ? apiAvailability.pipe(takeUntil(fromEvent(abortSignal, 'abort'))) : apiAvailability,
    ).catch((error: unknown) => {
        if (abortSignal?.aborted) {
            throw abortSignal.reason;
        }
        throw error;
    });

    if (availability.status === 'unauthenticated') {
        throw new LocatesAuthenticationUnavailableError();
    }
    return availability.api;
};

/**
 * Legacy locates client implementation used by the old LocatesWidget.
 *
 * This client uses RESTful HTTP calls with custom retry logic and Redux saga integration.
 * Data is fetched on bootstrap and stored in Redux, with real-time updates via WebSocket.
 *
 * For new features, prefer the modern React Query approach used in LAB Admin (see queries/labOrders.ts).
 */
export class LocatesClientLegacyImpl implements LocatesClient {
    constructor() {
        /*empty*/
    }

    async getLocatesProviders(abortSignal: AbortSignal): Promise<LocateProvider[]> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi(abortSignal);
        const { data } = await api.get<LocateProviderResponse>('/providers', { signal: abortSignal });
        return data.providers;
    }

    async getUploadableLocatesProviders(abortSignal: AbortSignal): Promise<UploadableLocateProvider[]> {
        const api = await getApi(abortSignal);
        const { data } = await api.get<UploadableLocateProviderResponse>('/inventory', { signal: abortSignal });
        return data.providers;
    }

    async getLocatesOrders(abortSignal: AbortSignal, limit?: number, page?: number): Promise<LocateOrder[]> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi(abortSignal);
        const { data } = await api.get<LocateOrdersForUserResponse>('/orders', {
            params: limit === undefined ? undefined : { size: limit, ...(page === undefined ? {} : { page }) },
            signal: abortSignal,
        });
        return data.orders;
    }

    async getLocatesAccounts(abortSignal: AbortSignal, accountIds?: string[]): Promise<LocateAccount[]> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi(abortSignal);
        const { data } = await api.post<LocatePlatformDiscoveryResponse>(
            '/platforms/discover',
            {
                accountIds: accountIds,
            } satisfies LocatePlatformDiscoveryRequest,
            { signal: abortSignal },
        );
        return data.accounts as LocateAccount[]; // TODOLOCATES: locates contracts use latest plat-common with deny permission support
    }

    async createLocatesOrder(order: NewLocateOrder, autoAcceptPrice?: number): Promise<LocateOrder[]> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi();
        const { data } = await api.post<NewLocateOrderResponse>('/orders', {
            order,
            autoAccept:
                typeof autoAcceptPrice === 'number'
                    ? {
                          enabled: true,
                          price: autoAcceptPrice,
                      }
                    : undefined,
        } satisfies NewLocateOrderRequest);
        return data.orders;
    }

    async injectLocate(receipts: LocateReceipt[], accountId: string): Promise<LocateOrder[]> {
        const api = await getApi();
        const { data } = await api.post<LocateInjectResponse>(`/platforms/accounts/${accountId}/inject`, {
            receipts,
        } satisfies LocateInjectRequest);
        return data.injections;
    }

    // TODOLOCATES PLAT-5357 - maybe change the response type to something more useful
    async uploadLocate(poolId: string, inventory: SecurityInventory[]): Promise<LocateInventoryPoolUploadResponse> {
        const api = await getApi();
        const { data } = await api.post<LocateInventoryPoolUploadResponse>(`/inventory/${poolId}/upload`, {
            inventory,
        } satisfies LocateInventoryPoolUploadRequest);
        return data;
    }

    async acceptLocatesOrder(orderId: string): Promise<LocateOrder> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi();
        const { data } = await api.post<LocateAcceptResponse>(`/orders/${orderId}/accept`);
        return data.order;
    }

    async rejectLocatesOrder(orderId: string): Promise<LocateOrder> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi();
        const { data } = await api.patch<LocateRejectResponse>(`/orders/${orderId}/reject`);
        return data.order;
    }

    async rejectAllLocatesOrders(accountIds: string[]): Promise<LocateOrder[]> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi();
        const { data } = await api.patch<ClearLocateOrdersResponse>(`/orders/clear`, {
            accountIds,
        } satisfies ClearLocateOrdersRequest);
        return data.orders;
    }

    async cancelLocatesOrder(orderId: string): Promise<LocateOrder> {
        // TODO: handle any expected errors and ensure destructuring is safe
        const api = await getApi();
        const { data } = await api.patch<LocateCancelResponse>(`/orders/${orderId}/cancel`);
        return data.order;
    }

    async changeAutoAccept(orderId: string, autoAccept: AutoAcceptInfo): Promise<LocateOrder> {
        const api = await getApi();
        const { data } = await api.put<LocateLimitResponse>(`/orders/${orderId}/limit`, {
            autoAccept,
        } satisfies LocateLimitRequest);
        return data.order;
    }
}
